import React from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import Close from '@mui/icons-material/Close';
import DateRange from '@mui/icons-material/DateRange';

import { DayPicker, TZDate } from 'react-day-picker';
import { formatISO, parseISO } from 'date-fns';

import { Button, ButtonGroup } from 'uikit/buttons';
import { Menu } from 'uikit/menus';
import { TextInput } from 'uikit/inputs';

import 'react-day-picker/dist/style.css';
import styles from '../styles/datePicker.module.scss';

const DatePicker = ({
  clearButtonText = '',
  doneButtonText = '',
  id,
  inputType = 'button',
  isDateTime = false,
  isDisabled = false,
  onChange = () => {},
  placeholder = '',
  value = '',
  verticalPosition = 'bottom',
}) => {
  const html = document.documentElement;
  const timeZone = isDateTime ? html.dataset.timezone : '';
  const handleClear = () => onChange('');

  const handleSelect = (date) => {
    if (!date) return;

    const isoString = isDateTime
      ? date.withTimeZone('UTC').toISOString().replace('.000', '')
      : formatISO(date, { representation: 'date' });

    onChange(isoString);
  };

  const placeholderText = placeholder || i18n.t('select-date');

  const dateValue = value
    ? new Date(value).toLocaleDateString(html.lang || 'en', {
        timeZone: timeZone || 'UTC',
      })
    : null;

  const component =
    inputType === 'text' ? (
      <TextInput
        icon={<DateRange sx={{ height: 18, width: 18 }} />}
        id={id}
        isDisabled={isDisabled}
        onChange={onChange}
        placeholder={placeholderText}
        value={value ?? ''}
      />
    ) : (
      <Button
        icon={<DateRange />}
        isDisabled={isDisabled}
        text={dateValue || placeholderText}
        variant="outlined"
      />
    );

  return (
    <div className={styles.element}>
      <Menu
        component={component}
        contentClassName={styles.dialog}
        horizontalPosition="left"
        shouldCloseOnTab={false}
        verticalPosition={verticalPosition}
      >
        <>
          <DayPicker
            mode="single"
            onSelect={handleSelect}
            selected={value && (isDateTime ? new TZDate(value, timeZone) : parseISO(value))}
            timeZone={timeZone}
          />
          <ButtonGroup className={styles.buttons}>
            <Button
              onClick={handleClear}
              size="small"
              text={clearButtonText || i18n.t('date-picker-clear')}
              variant="ghost"
            />
            <Button
              color="brand"
              size="small"
              text={doneButtonText || i18n.t('date-picker-done')}
            />
          </ButtonGroup>
        </>
      </Menu>
      {value && !isDisabled && (
        <Button
          ariaLabel={clearButtonText || i18n.t('date-picker-clear')}
          onClick={handleClear}
          icon={<Close />}
          size="small"
          variant="ghost"
        />
      )}
    </div>
  );
};

DatePicker.propTypes = {
  clearButtonText: PropTypes.string,
  doneButtonText: PropTypes.string,
  id: PropTypes.string.isRequired,
  inputType: PropTypes.oneOf(['button', 'text']),
  isDateTime: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  verticalPosition: PropTypes.oneOf(['auto', 'top', 'bottom']),
};

export default DatePicker;
